/**
 * AudioDebugOverlay - temporary on-device audio diagnostics
 *
 * Renders only when the URL contains ?audiodebug=1. Shows the shared
 * AudioContext's live state/clock (polled — state changes don't trigger
 * React re-renders), the iOS audioSession state, and the engine's
 * persisted event log with Copy/Clear. Exists because there's no desktop
 * devtools access to a phone in the field; see ios_AUDIO_FIX.md.
 */

import { useState, useEffect } from 'react'
import {
  getAudioEngineState,
  getAudioLog,
  clearAudioLog,
  subscribeAudioLog
} from './AudioEngine'

const AudioDebugOverlay = () => {
  const [engine, setEngine] = useState(getAudioEngineState())
  const [logVersion, setLogVersion] = useState(0)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const poll = setInterval(() => setEngine(getAudioEngineState()), 500)
    const unsubscribe = subscribeAudioLog(() => setLogVersion(v => v + 1))
    return () => {
      clearInterval(poll)
      unsubscribe()
    }
  }, [])

  const log = getAudioLog()

  const handleCopy = async () => {
    const header = `ctx=${engine.ctxState} clock=${engine.currentTime.toFixed(3)} sr=${engine.sampleRate} session=${engine.sessionType}/${engine.sessionState}`
    try {
      await navigator.clipboard.writeText([header, ...log].join('\n'))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API unavailable (non-secure context) — log stays on screen
    }
  }

  return (
    <div className="fixed bottom-2 left-2 right-2 z-[100] max-h-[40vh] flex flex-col rounded-lg bg-black/85 text-green-400 font-mono text-[11px] p-2 pointer-events-auto">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span>
          ctx=<b>{engine.ctxState}</b>{' '}
          clock=<b>{engine.currentTime.toFixed(3)}</b>{' '}
          sr={engine.sampleRate}{' '}
          session={engine.sessionType}/{engine.sessionState}
          {engine.needsReset && <b className="text-red-400"> RESET-PENDING</b>}
        </span>
        <span className="flex gap-1 shrink-0">
          <button
            onClick={handleCopy}
            className="px-2 py-0.5 rounded bg-green-700 text-white"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            onClick={clearAudioLog}
            className="px-2 py-0.5 rounded bg-gray-600 text-white"
          >
            Clear
          </button>
        </span>
      </div>
      <div className="overflow-y-auto whitespace-pre-wrap break-all" data-log-version={logVersion}>
        {log.length === 0 ? '(no audio events yet)' : log.slice(-60).join('\n')}
      </div>
    </div>
  )
}

export default AudioDebugOverlay
